import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { StorageSource } from '@vidmakr/bindings/types';
import {
  isFolderLinkedKey,
  parseFolderLinkedKey,
  parseLinkedKey,
  parseSourceConfig,
  planFolderMedia,
  type FolderPlan,
  type S3SourceConfig,
  type SourceListing,
  type WorkerSourceConfig,
} from '@vidmakr/shared';
import { api } from './api';

export type SourceRow = StorageSource;

/** The organization's storage connections the viewer can see (RLS hides the others), by name. */
export function useStorageSources(orgId: string | undefined) {
  const [rows, ready] = useTable(tables.storageSource.where((s) => s.orgId.eq(orgId ?? '')));
  const sources = useMemo(() => [...rows].sort((a, b) => a.name.localeCompare(b.name)), [rows]);
  return { sources, ready };
}

export function s3Config(s: SourceRow): S3SourceConfig | null {
  return s.kind === 's3' ? (parseSourceConfig('s3', s.config) as S3SourceConfig | null) : null;
}

export function workerConfig(s: SourceRow): WorkerSourceConfig | null {
  return s.kind === 'worker' ? (parseSourceConfig('worker', s.config) as WorkerSourceConfig | null) : null;
}

/** A short description of where a connection points. */
export function sourceWhere(s: SourceRow, workerName?: (id: string) => string | undefined): string {
  const c = s3Config(s);
  if (c) {
    const host = c.endpoint ? new URL(c.endpoint).host : 'AWS S3';
    return `${host} · ${c.bucket}${c.prefix ? `/${c.prefix.replace(/\/$/, '')}` : ''}`;
  }
  const w = workerConfig(s);
  if (w) return `${workerName?.(w.workerId) ?? 'Worker'} · ${w.root}${w.path ? `/${w.path}` : ''}`;
  return '';
}

export function listSource(id: string, path: string, cursor?: string) {
  const q = new URLSearchParams({ path });
  if (cursor) q.set('cursor', cursor);
  return api<SourceListing>(`/api/sources/${encodeURIComponent(id)}/list?${q}`);
}

const previewCache = new Map<string, { url: string; expiresAt: number }>();

/** Short-lived URLs to preview bucket files (cached until shortly before they expire). */
export async function previewUrls(id: string, paths: string[]): Promise<Record<string, string>> {
  const now = Date.now();
  const out: Record<string, string> = {};
  const missing: string[] = [];
  for (const p of paths) {
    const hit = previewCache.get(`${id}:${p}`);
    if (hit && hit.expiresAt > now) out[p] = hit.url;
    else missing.push(p);
  }
  for (let i = 0; i < missing.length; i += 100) {
    const res = await api<{ urls: Record<string, string>; expiresAt?: number }>(`/api/sources/${encodeURIComponent(id)}/preview`, {
      method: 'POST',
      json: { paths: missing.slice(i, i + 100) },
    });
    for (const [p, url] of Object.entries(res.urls)) {
      out[p] = url;
      previewCache.set(`${id}:${p}`, { url, expiresAt: (res.expiresAt ?? now + 3600_000) - 5 * 60_000 });
    }
  }
  return out;
}

export interface SourceForm {
  name: string;
  kind: 's3' | 'worker';
  spaceId: string;
  allowWrite: boolean;
  s3: { endpoint: string; region: string; bucket: string; prefix: string; forcePathStyle: boolean; accessKeyId: string; secretAccessKey: string };
  worker: { workerId: string; root: string; path: string };
}

export function saveSource(orgId: string, id: string | null, form: SourceForm) {
  return id
    ? api<{ id: string }>(`/api/sources/${encodeURIComponent(id)}`, { method: 'PUT', json: form })
    : api<{ id: string }>(`/api/sources/orgs/${encodeURIComponent(orgId)}`, { method: 'POST', json: form });
}

export function testSource(id: string) {
  return api<{ ok: boolean; error: string }>(`/api/sources/${encodeURIComponent(id)}/test`, { method: 'POST' });
}

export function verifyLinked(id: string) {
  return api<{ checked: number; missing: number }>(`/api/sources/${encodeURIComponent(id)}/verify`, { method: 'POST' });
}

export function saveExportToSource(id: string, jobId: string, folder: string) {
  return api<{ ok: boolean; path: string; bytes: number }>(`/api/sources/${encodeURIComponent(id)}/save-export`, { method: 'POST', json: { jobId, folder } });
}

/** The folder names a worker shares (from the org_workers view). */
export function workerRootNames(w: { sourceRoots: string }): string[] {
  try {
    const v = JSON.parse(w.sourceRoots || '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** The organization's workers the viewer may know about: every one for admins, the enabled ones (by name) for editors. */
export function useOrgWorkerInfo(orgId: string | undefined) {
  const [admin] = useTable(tables.orgWorkers);
  const [choices] = useTable(tables.orgWorkerChoices);
  return useMemo(() => {
    const m = new Map<string, { id: string; name: string; online: boolean; disabled: boolean; roots?: string[] }>();
    for (const w of choices) if (w.orgId === orgId) m.set(w.id, { id: w.id, name: w.name, online: w.online, disabled: false });
    for (const w of admin) if (w.orgId === orgId) m.set(w.id, { id: w.id, name: w.name, online: w.online, disabled: w.disabled, roots: workerRootNames(w) });
    return m;
  }, [admin, choices, orgId]);
}

/** Where a linked asset's original is kept: its connection, and for a network folder the worker sharing it. */
export function useLinkedOrigin(asset: { orgId: string; sourceId: string; storageKey: string } | undefined) {
  const { sources } = useStorageSources(asset?.orgId);
  const workers = useOrgWorkerInfo(asset?.orgId);
  if (!asset?.sourceId) return null;
  const nas = isFolderLinkedKey(asset.storageKey);
  const src = sources.find((s) => s.id === asset.sourceId);
  const w = src ? workerConfig(src) : null;
  const worker = w ? workers.get(w.workerId) : undefined;
  return {
    nas,
    /** Undefined when the viewer cannot see the connection (or it was removed). */
    connection: src?.name,
    path: (nas ? parseFolderLinkedKey(asset.storageKey) : parseLinkedKey(asset.storageKey))?.path ?? '',
    worker: worker?.name,
    workerOnline: worker?.online,
  };
}

export type FolderExport =
  | { kind: 'none' }
  /** Runs on this worker (`online` = it can start right away). */
  | { kind: 'worker'; plan: FolderPlan; online: boolean }
  /** Some connection is not visible to the viewer: the server picks (and checks) the worker. */
  | { kind: 'unknown' }
  | { kind: 'error'; error: string };

/**
 * Where an export of these assets must run when some are kept on a NAS (the
 * module decides and checks the same; this tells the export dialog early).
 */
export function useFolderExport(orgId: string | undefined, assets: Iterable<{ name: string; storageKey: string; sourceId?: string }>): FolderExport {
  const { sources } = useStorageSources(orgId);
  const workers = useOrgWorkerInfo(orgId);
  const list = [...assets].filter((a) => isFolderLinkedKey(a.storageKey));
  if (!list.length) return { kind: 'none' };
  const byId = new Map(sources.map((s) => [s.id, s]));
  if (list.some((a) => !byId.has(parseFolderLinkedKey(a.storageKey)?.sourceId ?? ''))) return { kind: 'unknown' };
  const plan = planFolderMedia(list, (id) => byId.get(id), (id) => workers.get(id));
  if (!plan) return { kind: 'none' };
  if ('error' in plan) return { kind: 'error', error: plan.error };
  return { kind: 'worker', plan, online: !!workers.get(plan.workerId)?.online };
}
